import { useEffect, useState } from "react";
import { Plus, Trash2, ShieldX, Search } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import SearchableSelect from "../../components/SearchableSelect";
import DocumentScannerBox from "../../components/DocumentScannerBox";

const TIPI = ["rca", "kasko", "furto", "incendio", "atti_vandalici", "kasko_collisione", "altro"];
const STATO = {
  aperto: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  denunciato: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  in_perizia: "bg-purple-500/15 text-purple-300 border-purple-500/30",
  liquidato: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  chiuso: "bg-white/10 text-white/60 border-white/20",
};

export default function Sinistri() {
  const [list, setList] = useState([]);
  const [cars, setCars] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [pageSearch, setPageSearch] = useState("");
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({
    car_id: "", contract_id: "", customer_id: "", data_sinistro: new Date().toISOString().slice(0, 10),
    luogo: "", descrizione: "", tipo: "rca",
    controparte_nome: "", controparte_targa: "", controparte_assicurazione: "",
    cid_compilato: false, polizza_n: "",
    stato: "aperto", importo_richiesto: 0, importo_liquidato: 0, franchigia_applicata: 0, note: "",
  });

  const load = async () => {
    const [s, c, ct, cu] = await Promise.all([api.get("/sinistri"), api.get("/cars"), api.get("/contracts"), api.get("/customers")]);
    setList(s.data); setCars(c.data); setContracts(ct.data); setCustomers(cu.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, importo_richiesto: +form.importo_richiesto, importo_liquidato: +form.importo_liquidato, franchigia_applicata: +form.franchigia_applicata };
      await api.post("/sinistri", payload);
      toast.success("Sinistro registrato");
      setShow(false); load();
    } catch (e) { toast.error("Errore"); }
  };

  const updateStato = async (s, stato) => {
    await api.put(`/sinistri/${s.id}`, { ...s, stato });
    load();
  };

  return (
    <div className="space-y-6" data-testid="sinistri-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-black uppercase tracking-tight flex items-center gap-3">
            Sinistri <ShieldX className="text-[#ff0033]" />
          </h1>
          <p className="text-sm text-white/45 mt-1">{list.length} sinistri · {list.filter(s => s.stato !== 'liquidato' && s.stato !== 'chiuso').length} aperti</p>
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input data-testid="sin-page-search" placeholder="Cerca cliente, targa, contratto…" value={pageSearch} onChange={(e) => setPageSearch(e.target.value)}
              className="bg-white/[0.03] border border-white/10 rounded-full pl-9 pr-4 py-2 text-sm w-72 focus:border-[#ff0033]/60 focus:outline-none" />
          </div>
          <button data-testid="sin-add" onClick={() => setShow(true)} className="btn-neon px-5 py-2 rounded-full text-xs inline-flex items-center gap-2">
            <Plus size={14} /> Nuovo Sinistro
          </button>
        </div>
      </div>

      <div className="glass rounded-2xl overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-white/[0.03] text-[10px] uppercase tracking-widest text-white/40">
            <tr>
              <th className="text-left p-4">Sinistro</th>
              <th className="text-left p-4">Veicolo · Contratto</th>
              <th className="p-4">Tipo</th>
              <th className="p-4">Data · Luogo</th>
              <th className="p-4 text-right">Importi</th>
              <th className="p-4">Stato</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {list.filter((s) => {
              if (!pageSearch) return true;
              const q = pageSearch.toLowerCase();
              const custName = (s.customer?.ragione_sociale || `${s.customer?.nome || ''} ${s.customer?.cognome || ''}`).toLowerCase();
              return [s.numero, s.car?.targa, s.car?.marca, s.car?.modello, s.contract?.numero, custName, s.luogo, s.descrizione, s.tipo].some((x) => (x || '').toLowerCase().includes(q));
            }).map((s) => (
              <tr key={s.id} className="hover:bg-white/[0.02]" data-testid={`sin-${s.numero}`}>
                <td className="p-4">
                  <div className="font-mono font-bold text-[11px]">{s.numero}</div>
                  <div className="text-[10px] text-white/40">op. {s.operatore_nome}</div>
                  {s.cid_compilato && <div className="text-[10px] text-emerald-300 mt-1">CID ✓</div>}
                </td>
                <td className="p-4">
                  <div className="font-medium">{s.car?.marca} {s.car?.modello}</div>
                  <div className="text-[10px] font-mono text-white/40">{s.car?.targa}</div>
                  {s.contract && <div className="text-[10px] text-white/40 mt-1">Contratto {s.contract.numero}</div>}
                </td>
                <td className="p-4 text-center text-xs uppercase tracking-wider">{s.tipo.replace('_', ' ')}</td>
                <td className="p-4">
                  <div className="text-xs font-mono">{s.data_sinistro}</div>
                  <div className="text-[10px] text-white/40 truncate max-w-[200px]">{s.luogo}</div>
                </td>
                <td className="p-4 text-right text-xs">
                  <div>Rich: <b>€{s.importo_richiesto || 0}</b></div>
                  <div className="text-emerald-300">Liq: €{s.importo_liquidato || 0}</div>
                  <div className="text-white/40">Fr: €{s.franchigia_applicata || 0}</div>
                </td>
                <td className="p-4 text-center">
                  <select value={s.stato} onChange={(e) => updateStato(s, e.target.value)}
                    className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${STATO[s.stato]} bg-transparent`}>
                    {["aperto", "denunciato", "in_perizia", "liquidato", "chiuso"].map((st) => <option key={st} className="bg-[#0a0a0a]">{st.replace('_', ' ')}</option>)}
                  </select>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan="6" className="p-10 text-center text-white/40">Nessun sinistro registrato.</td></tr>}
          </tbody>
        </table>
      </div>

      {show && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-6" onClick={() => setShow(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-2xl glass-strong rounded-3xl p-6 max-h-[92vh] overflow-auto" data-testid="sin-form">
            <div className="font-display text-2xl font-bold mb-5">Nuovo Sinistro</div>

            {/* SCANNER AI CID / PERIZIA */}
            <div className="mb-5">
              <DocumentScannerBox context="sinistri"
                testIdPrefix="sin-scan"
                title="Scansione AI · CID / Perizia"
                subtitle="→ Auto-compila Targa + Data + Importo"
                description="Carica foto o PDF del CID (Constatazione Amichevole di Incidente), perizia del danno, denuncia sinistro o documenti correlati. L'AI estrae targa, data sinistro, controparte e importi."
                variant="compact"
                onResult={(payload) => {
                  const v = payload?.veicolo || {};
                  const doc = payload?.documento || {};
                  setForm((f) => ({
                    ...f,
                    data_sinistro: doc.data_emissione || doc.data_violazione || f.data_sinistro,
                    polizza_n: doc.numero || f.polizza_n,
                    descrizione: payload?.note || f.descrizione,
                  }));
                  // Match veicolo in flotta per targa estratta
                  if (v.targa) {
                    const tg = v.targa.toUpperCase();
                    const match = cars.find((c) => (c.targa || "").toUpperCase() === tg);
                    if (match) {
                      setForm((f) => ({ ...f, car_id: match.id }));
                      toast.success(`Veicolo ${tg} associato dalla flotta`);
                    }
                  }
                }}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {/* === CLIENTE SEARCH (cross-link contratti) === */}
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-white/40">Cliente (cerca per nome, CF, P.IVA)</label>
                <SearchableSelect
                  testid="sin-cust"
                  value={form.customer_id}
                  onChange={(v) => setForm({ ...form, customer_id: v, contract_id: "" })}
                  placeholder="Digita nome, cognome, ragione sociale, CF o P.IVA…"
                  options={customers.map((c) => ({
                    value: c.id,
                    label: c.ragione_sociale || `${c.nome || ""} ${c.cognome || ""}`.trim() || "(senza nome)",
                    extra: c.codice_fiscale || c.piva || c.email || "",
                  }))}
                />
                {form.customer_id && (() => {
                  const c = customers.find((x) => x.id === form.customer_id);
                  if (!c) return null;
                  const custContracts = contracts.filter((ct) => ct.customer_id === form.customer_id);
                  return (
                    <div className="mt-2 px-3 py-2 rounded-lg bg-emerald-500/5 border border-emerald-500/30 text-xs">
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <div className="font-medium">{c.ragione_sociale || `${c.nome || ''} ${c.cognome || ''}`}</div>
                          <div className="text-[10px] text-white/45 font-mono">{c.codice_fiscale || c.piva || '—'}</div>
                        </div>
                        <button type="button" data-testid="sin-cust-clear" onClick={() => setForm({ ...form, customer_id: "", contract_id: "" })}
                          className="text-[10px] text-white/40 hover:text-[#ff0033]">✕ rimuovi</button>
                      </div>
                      {custContracts.length > 0 && (
                        <div className="mt-2 text-[10px] uppercase tracking-widest text-emerald-300/80">{custContracts.length} contratti collegati</div>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-white/40">Veicolo</label>
                <select required value={form.car_id} onChange={(e) => setForm({ ...form, car_id: e.target.value })}
                  className="mt-1 w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm">
                  <option value="">—</option>
                  {cars.map((c) => <option key={c.id} value={c.id}>{c.marca} {c.modello} · {c.targa}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-white/40">Contratto {form.customer_id ? "(solo del cliente selezionato)" : "(opzionale)"}</label>
                <select value={form.contract_id} onChange={(e) => setForm({ ...form, contract_id: e.target.value })}
                  data-testid="sin-contract-select"
                  className="mt-1 w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm">
                  <option value="">—</option>
                  {contracts
                    .filter((c) => !form.car_id || c.car_id === form.car_id)
                    .filter((c) => !form.customer_id || c.customer_id === form.customer_id)
                    .map((c) => (
                      <option key={c.id} value={c.id}>{c.numero}</option>
                    ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40">Data</label>
                <input required type="date" value={form.data_sinistro} onChange={(e) => setForm({ ...form, data_sinistro: e.target.value })}
                  className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40">Tipo</label>
                <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                  className="mt-1 w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm">
                  {TIPI.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <input placeholder="Luogo sinistro" required className="col-span-2 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm"
                value={form.luogo} onChange={(e) => setForm({ ...form, luogo: e.target.value })} />
              <textarea placeholder="Descrizione" required rows={2} className="col-span-2 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm"
                value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} />
              <input placeholder="Controparte (nome)" className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm"
                value={form.controparte_nome} onChange={(e) => setForm({ ...form, controparte_nome: e.target.value })} />
              <input placeholder="Controparte (targa)" className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono"
                value={form.controparte_targa} onChange={(e) => setForm({ ...form, controparte_targa: e.target.value.toUpperCase() })} />
              <input placeholder="Assicurazione controparte" className="col-span-2 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm"
                value={form.controparte_assicurazione} onChange={(e) => setForm({ ...form, controparte_assicurazione: e.target.value })} />
              <input placeholder="N. polizza nostra" className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono"
                value={form.polizza_n} onChange={(e) => setForm({ ...form, polizza_n: e.target.value })} />
              <label className="flex items-center gap-2 text-sm py-2">
                <input type="checkbox" checked={form.cid_compilato} onChange={(e) => setForm({ ...form, cid_compilato: e.target.checked })} />
                <span>CID compilato</span>
              </label>
              <input placeholder="Importo richiesto €" type="number" step="0.01" className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm"
                value={form.importo_richiesto} onChange={(e) => setForm({ ...form, importo_richiesto: e.target.value })} />
              <input placeholder="Franchigia applicata €" type="number" step="0.01" className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm"
                value={form.franchigia_applicata} onChange={(e) => setForm({ ...form, franchigia_applicata: e.target.value })} />
            </div>
            <div className="flex gap-3 mt-5 justify-end">
              <button type="button" onClick={() => setShow(false)} className="px-5 py-2.5 rounded-full text-sm bg-white/[0.04] border border-white/10">Annulla</button>
              <button data-testid="sin-form-submit" type="submit" className="btn-neon px-6 py-2.5 rounded-full text-sm">Registra</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
